import React from "react";
import type { Ticket, UserLimitedList, Services } from "../../generated-axios-client";
import { Card, Flex, Table, Tag, Typography } from "antd";
import type { TableColumnsType } from "antd";
import { statusLabelMap, statusColorMap } from "../../ticket/ticketConstants";
import { AssigneeAvatarGroup } from "../../../common/components/AssigneeAvatarGroup";
import { resolveTicketAssignees } from "../dashboard.utils";
import { routeNames } from "../../common/routes";
import { palette } from "../../theme";

type Props = {
  tickets: Ticket[];
  assigneeById: Record<number, UserLimitedList>;
  serviceById: Record<string, Services>;
  loading?: boolean;
  onNavigate: (route: string, id: string) => void;
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

export const OverdueWorkOrdersTable: React.FC<Props> = ({
  tickets,
  assigneeById,
  serviceById,
  loading,
  onNavigate,
}) => {
  const columns: TableColumnsType<Ticket> = [
    {
      title: "Work Order",
      key: "name",
      render: (_: unknown, ticket: Ticket) => {
        const ticketId = ticket.id;
        const woLabel = (
          <Flex vertical gap={2}>
            <Typography.Text strong style={{ fontSize: 13 }}>
              {ticket.name ?? `#${ticket.ticket_number}`}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              #{ticket.ticket_number}
            </Typography.Text>
          </Flex>
        );
        return ticketId ? (
          <Typography.Link
            onClick={() => onNavigate(routeNames.TICKET_VIEW, ticketId)}
          >
            {woLabel}
          </Typography.Link>
        ) : (
          woLabel
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (status: string) => (
        <Tag color={statusColorMap[status] ?? "default"} style={{ fontWeight: 600 }}>
          {statusLabelMap[status] ?? status}
        </Tag>
      ),
    },
    {
      title: "Service",
      dataIndex: "service",
      key: "service",
      width: 140,
      render: (serviceId: string) => {
        const svc = serviceId ? serviceById[serviceId] : undefined;
        return svc ? (
          <Tag>{svc.name}</Tag>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        );
      },
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      key: "deadline",
      width: 110,
      render: (deadline: string) => (
        <Typography.Text style={{ color: palette.dashRed, fontWeight: 600, fontSize: 13 }}>
          {formatDate(deadline)}
        </Typography.Text>
      ),
    },
    {
      title: "Assignees",
      key: "assignees",
      width: 110,
      render: (_: unknown, ticket: Ticket) => (
        <AssigneeAvatarGroup assignees={resolveTicketAssignees(ticket, assigneeById)} />
      ),
    },
  ];

  return (
    <Card
      title={
        <Flex align="center" gap={8}>
          <span>Overdue Work Orders</span>
          {!loading && <Tag color="red">{tickets.length}</Tag>}
        </Flex>
      }
      loading={loading}
    >
      <Table
        dataSource={tickets}
        columns={columns}
        rowKey="id"
        size="small"
        pagination={{
          pageSize: 5,
          showSizeChanger: false,
          showTotal: (total) => `Total ${total} overdue`,
          size: "small",
          position: ["bottomRight"],
        }}
        scroll={{ x: true }}
        locale={{ emptyText: "No overdue work orders" }}
      />
    </Card>
  );
};
